/* eslint-disable no-unused-vars */

import React, {
  useState,
  useEffect,
  useReducer,
  useRef,
  forwardRef,
  useImperativeHandle,
  useLayoutEffect,
} from 'react';
import PropTypes from 'prop-types';
import ReactTooltip from 'react-tooltip';
import { Div, Icon, Input, Textarea, Label, Switch, Text } from '@pinpoint/design-kit';
import {
  getRelatedWords,
  getPercentile,
  getTraits,
  getWordVector,
  getWordModel,
  getPercentileFromMap,
  getWordSimilaritiesFromMap,
  getWordDistancesFromMap,
  getMaskedWords,
  getWordVectors,
} from 'src/store/api';
import * as mathjs from 'mathjs';

import Slider from './slider';

/* eslint-disable no-console */

const timeoutsQueue = [];

const DEFAULT_REFRESH = {
  BERTTWEET_MODEL: 'RF32',
  WORD_2_VEC: 'RF32',
};

const SET_ID = {
  WORD_2_VEC: 1,
  BERTTWEET_MODEL: 3,
};

const NUM_COEFFICIENTS = {
  WORD_2_VEC: 100,
  BERTTWEET_MODEL: 768,
};

const WVM_ID = {
  WORD_2_VEC: 1,
  BERTTWEET_MODEL: 2,
};

const WORDS_MINIMUM_BOUND = {
  WORD_2_VEC: 1,
  BERTTWEET_MODEL: 6,
};

const DEFAULT_MODEL = localStorage.getItem('COPYPX_DL_MODEL') ? 'BERTTWEET_MODEL' : 'WORD_2_VEC';

const logistic = (logit) => 1 / (1 + mathjs.exp(-logit));

const WORD_2_VEC = 'WORD_2_VEC';
const MASKED_MODEL = 'MASKED_MODEL';
const BERTTWEET_MODEL = 'BERTTWEET_MODEL';

const MODELS = [WORD_2_VEC, MASKED_MODEL];

/*
 *
 * This now obsolete method get the percentile from probability
 * by calling a backend api
 *
 * This is no longer used to minimize the network cost
 *
const getPercentileFromProbability = async (refreshId, traitProbabilities) => {
  const andFilters = traitProbabilities
    .map(({ traitCode, probability }) => ({
      and: [
        {
          name: 'trait__code',
          op: 'has',
          val: traitCode,
        },
        {
          name: 'probability',
          op: 'eq',
          val: probability.toFixed(2),
        },
      ],
    }));
  const filters = [{
    or: andFilters,
  }];
  const query = { filters };
  const items = await getPercentile(query);
  return items;
};
*/

/*
 *
 * This method is used to get the trait probabilities of the copy text
 * just to log it for debugging purposes
 *
 */
const getTraitProbabilities = async (wordVector, traitCodes, traits, traitCoeffs, traitIntercepts) => {
  // 2. Normalize the centroid
  const wordVectorNorm = mathjs.divide(wordVector, mathjs.norm(wordVector));

  // Do the dotproduct with the model coefficients
  // and add the intercept
  const traitProbabilities = traitCodes.map((code) => ({
    trait: traits[code],
    traitCode: code,
    score: logistic(mathjs.chain(wordVectorNorm).dot(traitCoeffs[code]).add(traitIntercepts[code]).done()),
  }));

  return traitProbabilities;
};

/*
 *
 * This method uses the refresh-percentile-probability values stored in the
 * repository to compute the percentile from the probability
 *
 */
const getPercentileFromProbability = async (refreshId, traitProbabilities) =>
  traitProbabilities.map(({ trait, probability }) => {
    const percentile = getPercentileFromMap(refreshId, trait.id, probability.toFixed(2));
    return {
      percentile,
      probability: probability.toFixed(2),
      traitId: trait.id,
      trait: {
        id: trait.id,
        code: trait.code,
      },
    };
  });

/*
 * Given two sorted PX scores, extracts the common traits between the
 * two scores and return as two vectors
 *
 */
const getVectorsFromPxScores = (traitCodes, pxScores1, pxScores2) => {
  const vector1 = [];
  const vector2 = [];
  let exited = false;
  let counter1 = 0;
  let counter2 = 0;
  while (!exited) {
    const { trait: trait1, score: score1 } = pxScores1[counter1];
    const { trait: trait2, score: score2 } = pxScores2[counter2];
    if (trait1.code === trait2.code) {
      counter1 += 1;
      counter2 += 1;
      vector1.push(score1);
      vector2.push(score2);
    } else if (traitCodes.indexOf(trait1.code) < traitCodes.indexOf(trait2.code)) {
      counter2 += 1;
    } else {
      counter1 += 1;
    }
    exited = counter1 >= pxScores1.length || counter2 >= pxScores2.length;
  }
  return {
    vector1,
    vector2,
  };
};

/*
 * This computes the euclidean distance or l2-norm between two PX Scores. PX score
 * is a object with following fields
 *
 *  * score
 *  * trait object which includes code (trait code)
 *
 *  This along with `getCosineSimilarity` or two ways of comparing two fingerprints
 *
 */
const getPxScoresDistance = (traitCodes, pxScores1, pxScores2) => {
  if (pxScores1.length === 0 || pxScores2.length === 0) return null;
  const { vector1, vector2 } = getVectorsFromPxScores(traitCodes, pxScores1, pxScores2);
  return mathjs.distance(vector1, vector2);
};

/*
 * This computes the cosine similarity between two PX Scores. PX score
 * is a object with following fields
 *
 *  * score
 *  * trait object which includes code (trait code)
 *
 *  This along with `getCosineSimilarity` or two ways of comparing two fingerprints
 *
 */

const cosineSimilarity = (traitCodes, pxScores1, pxScores2) => {
  if (pxScores1.length === 0 || pxScores2.length === 0) return null;
  const { vector1, vector2 } = getVectorsFromPxScores(traitCodes, pxScores1, pxScores2);
  return mathjs.chain(vector1).dot(vector2).divide(mathjs.norm(vector1)).divide(mathjs.norm(vector2)).done();
};

/*
 * Computes the Raw Probabilities for a given word
 *
 */
const getTraitProbabilitiesFromWordVec = async (wordVector, traitCodes, traits, traitCoeffs, traitIntercepts) => {
  // Normalize the centroid
  const wordVectorNorm = mathjs.divide(wordVector, mathjs.norm(wordVector));

  // Do the dotproduct with the model coefficients
  // and add the intercept
  const traitProbabilities = traitCodes.map((code) => ({
    trait: traits[code],
    traitCode: code,
    probability: logistic(mathjs.chain(wordVectorNorm).dot(traitCoeffs[code]).add(traitIntercepts[code]).done()),
  }));

  return traitProbabilities;
};

/*
 * Computes the Px from a word vector. The PX can be computed without
 * making any network calls
 *
 */
const getPxFromWordVec = async (wordVector, traitCodes, traits, traitCoeffs, traitIntercepts) => {
  const traitProbabilities = await getTraitProbabilitiesFromWordVec(
    wordVector,
    traitCodes,
    traits,
    traitCoeffs,
    traitIntercepts
  );

  const percentiles = await getPercentileFromProbability(3, traitProbabilities);

  percentiles.sort(
    ({ trait: traitA }, { trait: traitB }) => traitCodes.indexOf(traitA.code) - traitCodes.indexOf(traitB.code)
  );

  let pxScores = [];
  if (percentiles.length > 0) {
    pxScores = percentiles.map(({ trait: { code }, percentile }) => ({
      trait: traits[code],
      traitCode: code,
      score: percentile,
    }));
  }

  return pxScores;
};

const getPredictedPx = async (
  centroidVector,
  totalWords,
  nextWordVector,
  oldWordVector,
  traitCodes,
  traits,
  traitCoeffs,
  traitIntercepts,
  nextText,
  pxModel
) => {
  let adjustedVector;
  if (pxModel === BERTTWEET_MODEL) {
    // Find the score for the new text
    const response = await getWordVectors(nextText, WVM_ID[pxModel]);
    adjustedVector = response[0].vector;
  } else if (pxModel === WORD_2_VEC) {
    // 1. Simulate `oldWord` removal and `nextWord` addition
    adjustedVector = mathjs
      .chain(centroidVector)
      .multiply(totalWords)
      .add(nextWordVector)
      .subtract(oldWordVector)
      .done();
  } else {
    throw new Error('Unknown model');
  }
  // 2. Call `getPxFromWordsVec` on new wordVectorSum
  const predictedPx = await getPxFromWordVec(adjustedVector, traitCodes, traits, traitCoeffs, traitIntercepts);

  // 3. Get the px
  return predictedPx;
};

const getCentroidVector = async (text, wordsVector, wordsFreq, pxModel) => {
  const words = Object.keys(wordsVector);
  if (pxModel === WORD_2_VEC) {
    let wordsVectorSum = new Array(NUM_COEFFICIENTS[pxModel]).fill(0);
    let totalWords = 0;
    for (let i = 0; i < words.length; i += 1) {
      if (wordsVector[words[i]].length === NUM_COEFFICIENTS[pxModel]) {
        wordsVectorSum = mathjs
          .chain(wordsVectorSum)
          .add(mathjs.multiply(wordsVector[words[i]], wordsFreq[words[i]]))
          .done();
        totalWords += wordsFreq[words[i]];
      }
    }
    const centroidVector = mathjs.divide(wordsVectorSum, totalWords);
    return { totalWords, centroidVector };
  }
  if (pxModel === BERTTWEET_MODEL) {
    const response = await getWordVectors(text, WVM_ID[pxModel]);
    const centroidVector = response[0].vector;
    return { centroidVector, totalWords: text.split(' ').length };
  }
  throw new Error('Unknown model');
};

/*
 * Computes the Px from a bag of words. Once the centroid of the bag of words
 * is calculated, the method `getPxFromWordVec` can be used to get the PX
 * from the word vector.
 *
 */
const getPxFromWordsVec = async (wordsVector, wordsFreq, traitCodes, traits, traitCoeffs, traitIntercepts) => {
  // 1. Get the centroid
  const words = Object.keys(wordsVector);
  let wordsVectorSum = new Array(100).fill(0);
  let totalWords = 0;
  for (let i = 0; i < words.length; i += 1) {
    if (wordsVector[words[i]].length === 100) {
      wordsVectorSum = mathjs
        .chain(wordsVectorSum)
        .add(mathjs.multiply(wordsVector[words[i]], wordsFreq[words[i]]))
        .done();
      totalWords += wordsFreq[words[i]];
    }
  }
  const wordsVectorCentroid = mathjs.divide(wordsVectorSum, totalWords);

  return getPxFromWordVec(wordsVectorCentroid, traitCodes, traits, traitCoeffs, traitIntercepts);
};

/*
 * Computes the average Px score by find the percentile of all traits
 * at 0.5 probability
 *
 */
const getAveragePxScores = async (traitCodes, traits) => {
  const traitProbabilities = traitCodes.map((code) => ({
    trait: traits[code],
    traitCode: code,
    probability: 0.5,
  }));
  const percentiles = await getPercentileFromProbability(3, traitProbabilities);

  percentiles.sort(
    ({ trait: traitA }, { trait: traitB }) => traitCodes.indexOf(traitA.code) - traitCodes.indexOf(traitB.code)
  );

  let pxScores = [];
  if (percentiles.length > 0) {
    pxScores = percentiles.map(({ trait: { code }, percentile }) => ({
      trait: traits[code],
      traitCode: code,
      score: percentile,
    }));
  }

  return pxScores;
};

/*
 * Given a list of related words with it's vectors, computes the PX
 * pf the words and then compute the distance / similarity from the
 * dataset px (if present) and orders the related words with more
 * similar words at the top and less similar ones at the bottom.
 *
 */
const getWordsOrderedByCloseness = async (
  relatedWords,
  comparisonPxScores,
  traitCodes,
  traits,
  traitCoeffs,
  traitIntercepts,
  centroidVector,
  totalWords,
  rootWordVector,
  text,
  rootWordStart,
  rootWordEnd,
  pxModel
) => {
  const wordsScoreList = await Promise.all(
    relatedWords.map(async ({ word, vector, similarity: textSimilarity }) => {
      let distance = 1 - textSimilarity;
      let targetDistance = 0;
      let targetSimilarity = 0;
      const wordPxSimilarity = 0;
      const wordPxDistance = 0;
      const wordPx = null;
      let predictedPx = null;
      if (comparisonPxScores.length > 0) {
        const nextText = `${text.slice(0, rootWordStart)}${word}${text.slice(rootWordEnd, text.length)}`;
        predictedPx = await getPredictedPx(
          centroidVector,
          totalWords,
          vector,
          rootWordVector,
          traitCodes,
          traits,
          traitCoeffs,
          traitIntercepts,
          nextText,
          pxModel
        );
        // We don't compute the wordPx anymore
        /*
        wordPx = await getPxFromWordVec(
          vector,
          traitCodes,
          traits,
          traitCoeffs,
          traitIntercepts,
        );
        */
        /*
         * The difference between the words and the
         * dataset PX can be either computed as l2-norm
         * or cosine similarity
         */
        targetDistance = getPxScoresDistance(traitCodes, predictedPx, comparisonPxScores);
        targetSimilarity = cosineSimilarity(traitCodes, predictedPx, comparisonPxScores);
        // We don't do wordPx similarities anymore
        /*
        wordPxSimilarity = cosineSimilarity(
          traitCodes,
          wordPx,
          comparisonPxScores,
        );
        wordPxDistance = getPxScoresDistance(
          traitCodes,
          wordPx,
          comparisonPxScores,
        );
        */
        // distance = 1 - targetSimilarity;
        distance = targetDistance;
      }
      return {
        word,
        wordPx,
        predictedPx,
        distance,
        vector,
        textSimilarity,
        targetSimilarity,
        targetDistance,
        wordPxSimilarity,
        wordPxDistance,
      };
    })
  );
  wordsScoreList.sort(({ distance: dist1 }, { distance: dist2 }) => dist1 - dist2);
  return wordsScoreList;
};

const logPxScores = (message, traitCodes, pxScores) => {
  if (pxScores && pxScores.length > 0) {
    const map = new Map();
    traitCodes.forEach((traitCode) => {
      const pxScore = pxScores.find(({ score, trait: { code } }) => code === traitCode);
      map.set(traitCode, pxScore ? pxScore.score : '-');
    });
    console.log(message, map);
  }
};

const logTraitProbabilities = (message, traitCodes, traitProbabilities) => {
  if (traitProbabilities && traitProbabilities.length > 0) {
    const map = new Map();
    traitCodes.forEach((traitCode) => {
      const prob = traitProbabilities.find(({ score, trait: { code } }) => code === traitCode);
      map.set(traitCode, prob ? prob.probability : '-');
    });
    console.log(message, map);
  }
};

const getPercentileOfScore = (array, value) =>
  array.length > 0
    ? array.reduce((accum, val) => accum + (val < value ? 1 : 0) + (val === value ? 0.5 : 0), 0) * (100 / array.length)
    : 0;

/*
 * Compute the similarity score for the progress bar between two
 * px scores. The absolute similarity is compared against a distribution
 * to figure its precentile and percentile is represented in the
 * progress bar
 */

const computeDistancePercentile = (state, pxScores1, pxScores2) => {
  const distanceDist = getWordDistancesFromMap(WVM_ID[state.pxModel], 3);
  const distanceScore = getPxScoresDistance(state.traitCodes, pxScores1, pxScores2);
  const distancePercentile = 100 - getPercentileOfScore(distanceDist, distanceScore);
  return { distanceScore, distancePercentile };
};

/*
 * Compute the similarity score for the progress bar between two
 * px scores. The absolute similarity is compared against a distribution
 * to figure its precentile and percentile is represented in the
 * progress bar
 */

const computeCosineSimilarityPercentile = (state, pxScores1, pxScores2) => {
  const similarityDist = getWordSimilaritiesFromMap(WVM_ID[state.pxModel], 3);
  const cosineSimScore = cosineSimilarity(state.traitCodes, pxScores1, pxScores2);
  const cosineSimPercentile = getPercentileOfScore(similarityDist, cosineSimScore);
  return { cosineSimScore, cosineSimPercentile };
};

/*
 * Controls the opacity of the `Div` expressing the similarity between
 * the word and the datset. The opacity ranges from 1 to `APLHA`
 *
 */
const ALPHA = 0.2;

const onTextChange = async (state, dispatch, value) => {
  // Record the change
  // console.log('RECORD');
  dispatch({ type: 'RECORD' });
  dispatch({ type: 'PROCESSING', payload: true });
  const safeWords = value
    .toLowerCase()
    .replace(/[^\w\s]/gi, '')
    .trim()
    .split(/\W+/);
  const safeWordsFreq = {};
  safeWords.forEach((word) => {
    safeWordsFreq[word] = (safeWordsFreq[word] || 0) + 1;
  });
  const vectorWords = Object.keys(state.wordsVector);
  const newWords = safeWords.filter(
    (safeWord) => !vectorWords.includes(safeWord) || safeWordsFreq[safeWord] > state.wordsFreq[safeWord]
  );
  const exiledWords = vectorWords.filter(
    (vectorWord) => !safeWords.includes(vectorWord) || safeWordsFreq[vectorWord] < state.wordsFreq[vectorWord]
  );
  /*
   * TODO: Optimize the workflow when a huge chunk of text is
   * copy - pasted by calling the api for multiple words at
   * a time
   *
   */
  try {
    await Promise.all(
      newWords
        .filter((word) => word)
        .map(async (word) => {
          if (state.pxModel === WORD_2_VEC) {
            let resp = null;
            if (Object.prototype.hasOwnProperty.call(state.wordVectorCache, word)) {
              resp = {
                vector: state.wordVectorCache[word],
              };
            } else {
              resp = await getWordVector(word, false, WVM_ID[state.pxModel]);
              dispatch({ type: 'ADD_TO_CACHE', payload: { word, vector: resp.vector } });
            }
            if (resp) {
              const freq = safeWordsFreq[word];
              console.log('Added word', word, ' with count', freq);
              dispatch({ type: 'ADD_WORD', payload: { word, vector: resp.vector, freq } });
            }
          }
        })
    );
  } catch (error) {
    dispatch({ type: 'SET_ERROR', payload: { hasError: true, error } });
  }
  exiledWords.forEach((word) => {
    console.log('Removed word', word, 'for ', state.wordsFreq[word] - (safeWordsFreq[word] || 0), ' occurrences');
    dispatch({
      type: 'REMOVE_WORD',
      payload: {
        word,
        freq: state.wordsFreq[word] - (safeWordsFreq[word] || 0),
      },
    });
  });
  // Compute the PX right now
  dispatch({ type: 'COMPUTE_PX', payload: true });
};

const prefixedNumber = (number, prec = 1) => `${number > 0 ? '+' : ''}${number.toFixed(prec)}`;

/* eslint-disable react/prop-types */

function Popup({
  top,
  left,
  rootWord,
  onClose,
  onClick,
  enableDiagnostics,
  copyTargetDistance,
  distanceDist,
  popupModel,
  state,
  dispatch,
}) {
  const popupRef = useRef(null);
  const [searchStr, setSearchStr] = useState('');
  const [value, setValue] = useState(65);
  const [model, setModel] = useState(state.totalWords === 1 ? WORD_2_VEC : popupModel);
  const [words, setWords] = useState([]);
  const [N, setN] = useState(0);
  const [processing, setProcessing] = useState(true);

  useLayoutEffect(() => {
    ReactTooltip.rebuild();
  });

  useEffect(() => {
    if (state.totalWords === 1 && model !== WORD_2_VEC) {
      setModel(WORD_2_VEC);
    }
  }, [state, model]);

  useEffect(() => {
    const func = async () => {
      try {
        const safeWord = rootWord
          .toLowerCase()
          .replace(/[^\w\s]/gi, '')
          .trim();
        let relatedWordsOrdered = [];
        let nextRelatedWords = [];
        if (model === WORD_2_VEC) {
          // Always get the word to vec
          const resp = await getWordVector(safeWord, true, WVM_ID.WORD_2_VEC);
          nextRelatedWords = resp.related_words;
        }
        if (model === MASKED_MODEL) {
          const sequence = `${state.text.slice(0, state.rootWordStart)}[MASK]${state.text.slice(
            state.rootWordEnd,
            state.text.length
          )}`;
          // console.log(sequence);
          const resp = await getMaskedWords(sequence, WVM_ID[state.pxModel]);
          nextRelatedWords = resp.substitutions;
        }
        if (nextRelatedWords && nextRelatedWords.length !== 0) {
          relatedWordsOrdered = await getWordsOrderedByCloseness(
            nextRelatedWords,
            state.comparisonPxScores,
            state.traitCodes,
            state.traits,
            state.traitCoeffs,
            state.traitIntercepts,
            state.centroidVector,
            state.totalWords,
            state.wordVectorCache[safeWord],
            state.text,
            state.rootWordStart,
            state.rootWordEnd,
            state.pxModel
          );
          setWords(relatedWordsOrdered);
          setN(relatedWordsOrdered.length);
          setProcessing(false);
        }
      } catch (error) {
        console.error(error);
        setProcessing(false);
        dispatch({ type: 'SET_ERROR', payload: { hasError: true, error } });
      }
    };
    func();
    // NOTE: needs to run once on load
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [model]);

  useEffect(() => {
    function handleOutsideClick(event) {
      if (popupRef.current && !popupRef.current.contains(event.target)) {
        onClose();
      }
    }
    document.addEventListener('mousedown', handleOutsideClick);
    return () => {
      document.removeEventListener('mousedown', handleOutsideClick);
    };
  }, [popupRef, onClose]);

  return (
    <div
      ref={(el) => {
        popupRef.current = el;
      }}
      style={{
        position: 'absolute',
        zIndex: '10',
        top,
        left,
      }}
    >
      <Div
        d="flex"
        flexDir="row"
        style={{
          position: 'absolute',
          zIndex: '10',
          top: '-5.25rem',
          left: '-4.0rem',
          backdropFilter: 'blur(1px)',
          borderRadius: '50%',
          background: 'rgba(255,255,255,.5)',
        }}
        p={{ x: '1rem', y: '1rem' }}
      >
        <Div w="8rem" d="flex" flexDir="row" align="center" m={{ x: '0.5rem' }}>
          <Slider
            disabled={model === MASKED_MODEL}
            min={50}
            max={80}
            h=".75rem"
            value={value}
            onChange={(e) => {
              setValue(e.target.value);
            }}
            activeColor="blue"
          />
          <Div
            d="flex"
            align="center"
            justify="center"
            h="1rem"
            w="1rem"
            m={{ x: '0.5rem' }}
            cursor="default"
            data-tip={
              model === MASKED_MODEL
                ? 'Toggle to activate slider'
                : 'Slide right for more similar words, left for more options.'
            }
          >
            <Icon fill="tabRest" name="QuestionMark" size="1rem" />
          </Div>
        </Div>
        <Div d="flex" flexDir="row" align="center" m={{ x: '0.5rem' }}>
          <Label
            tag="div"
            align="center"
            flexDir="row"
            flexWrap="nowrap"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              if (state.totalWords === 1) {
                if (model === MASKED_MODEL) {
                  setProcessing(true);
                  setModel(WORD_2_VEC);
                  setWords([]);
                }
              } else {
                setProcessing(true);
                setModel((currentModel) => (currentModel === MASKED_MODEL ? WORD_2_VEC : MASKED_MODEL));
                setWords([]);
              }
            }}
          >
            <Text>Related&nbsp;&nbsp;&nbsp;</Text>
            <Switch
              cursor={state.totalWords !== 1 ? 'cursor' : 'not-allowed'}
              checked={model === MASKED_MODEL}
              activeColor={state.totalWords !== 1 ? 'blue' : 'tabDisabled'}
              inactiveColor={state.totalWords !== 1 ? 'blue' : 'tabDisabled'}
              activeShadow="0"
              inactiveShadow="0"
            />
            <Text>Contextual</Text>
          </Label>
          <Div
            d="flex"
            align="center"
            justify="center"
            h="1rem"
            w="1rem"
            m={{ x: '0.5rem' }}
            cursor="default"
            data-tip={
              state.totalWords === 1
                ? 'Need more than one word to activate contextual suggestions.'
                : 'Keep toggle on the right for sentence-contextual substitutions; slide left for semantically related words.'
            }
          >
            <Icon fill="tabRest" name="QuestionMark" size="1rem" />
          </Div>
        </Div>
      </Div>
      <Div d="flex" flexDir="column" bg="white" maxH="20rem" overflow="scroll" rounded="md" shadow="5">
        {processing ? (
          <Div p="0.5rem" textSize="subheader" hoverBg="gray200" d="flex" align="center">
            Loading
            <Icon name="Loading4" viewBox="0 0 18 4" m={{ x: '0.4rem' }} />
          </Div>
        ) : N === 0 ? (
          <Div
            textColor="gray800"
            p="0.8rem"
            textSize="subheader"
            hoverBg="gray200"
            d="flex"
            align="center"
            style={{
              fontStyle: 'italic',
            }}
          >
            No suggestions
          </Div>
        ) : (
          <>
            <Input
              value={searchStr}
              onChange={(event) => {
                setSearchStr(event.target.value);
              }}
              suffix={<Icon name="Search" pos="absolute" top="50%" transform="translateY(-50%)" right="0.50rem" />}
            />
            {words
              .filter(
                (word) =>
                  (!searchStr || searchStr.trim().length === 0 || word.word.search(searchStr) !== -1) &&
                  word.textSimilarity >= value / 100
              )
              .map((word) => (
                <Div
                  p="0.5rem"
                  textSize="subheader"
                  cursor="pointer"
                  hoverBg="gray200"
                  d="flex"
                  align="center"
                  justify="space-between"
                  onClick={() => {
                    onClick(word.word, word.vector);
                  }}
                >
                  {localStorage.getItem('COPYPX_DIAGNOSTIC') && enableDiagnostics
                    ? `${word.word} | ${word.textSimilarity.toFixed(3)} | ${word.targetSimilarity.toFixed(
                        3
                      )} | ${word.wordPxSimilarity.toFixed(3)} | ${word.wordPxDistance.toFixed(3)}`
                    : word.word}
                  <Div d="flex" align="center" justify="space-between">
                    {
                      /* eslint-disable max-len */
                      state.comparisonPxScores.length > 0 ? (
                        <>
                          <Div
                            bg={word.targetDistance > copyTargetDistance ? 'tableauBlue' : 'tableauOrange'}
                            m={{ r: '.5rem', l: '1rem' }}
                            w="0.75rem"
                            h="0.75rem"
                            style={{
                              borderRadius: '50%',
                              opacity:
                                word.targetDistance > copyTargetDistance
                                  ? (1 -
                                      (words[N - 1].targetDistance - word.targetDistance) /
                                        (words[N - 1].targetDistance - copyTargetDistance)) *
                                      (1 - ALPHA) +
                                    ALPHA
                                  : (1 -
                                      (word.targetDistance - words[0].targetDistance) /
                                        (copyTargetDistance - words[0].targetDistance)) *
                                      (1 - ALPHA) +
                                    ALPHA,
                            }}
                          />
                          <Div d="flex" justify="flex-end" w="3rem" m={{ r: 0, l: 'auto' }}>
                            {prefixedNumber(
                              getPercentileOfScore(distanceDist, copyTargetDistance) -
                                getPercentileOfScore(distanceDist, word.targetDistance)
                            )}
                          </Div>
                        </>
                      ) : null
                      /* eslint-enable max-len */
                    }
                  </Div>
                </Div>
              ))}
          </>
        )}
      </Div>
    </div>
  );
}

const reducer = (state, action) => {
  switch (action.type) {
    case 'RECORD': {
      // Your preset has changed. Store
      // the present state in the past.
      // You have no future now.
      // console.log(state.past, state.future);
      return {
        ...state,
        historicalText: state.text,
        past: [
          ...state.past,
          {
            text: state.historicalText,
            wordsVector: state.wordsVector,
            wordsFreq: state.wordsFreq,
          },
        ],
        future: [],
      };
    }
    case 'UNDO': {
      // You want to go back to past.
      // pop a entry from past and set it
      // to preset and move the preset to
      // the future

      if (state.past.length === 0) return state;
      console.log(state.past, state.future);
      return {
        ...state,
        computePxScores: false,
        past: state.past.slice(0, state.past.length - 1),
        future: [
          {
            text: state.text,
            wordsVector: state.wordsVector,
            wordsFreq: state.wordsFreq,
          },
          ...state.future,
        ],
        ...state.past[state.past.length - 1],
      };
    }
    case 'REDO': {
      // You want to go to the future.
      // Dequeue an entry from future
      // and set it to the present. The
      // present becomes the past

      if (state.future.length === 0) return state;
      console.log(state.past, state.future);
      return {
        ...state,
        computePxScores: false,
        past: [
          ...state.past,
          {
            text: state.text,
            wordsVector: state.wordsVector,
            wordsFreq: state.wordsFreq,
          },
        ],
        future: state.future.slice(1),
        ...state.future[0],
      };
    }
    case 'SET_ERROR': {
      return {
        ...state,
        ...action.payload,
      };
    }
    case 'CLOSE_RELATED_POPUP': {
      return {
        ...state,
        isRelatedPopupOpen: false,
        relatedPopupCoords: { top: 0, left: 0 },
        relatedPopupIndex: 0,
      };
    }
    case 'OPEN_RELATED_POPUP': {
      return {
        ...state,
        isRelatedPopupOpen: true,
        relatedPopupCoords: {
          top: action.payload.top,
          left: action.payload.left,
        },
        relatedPopupIndex: action.payload.relatedPopupIndex,
      };
    }
    case 'REPLACE_WORD': {
      return {
        ...state,
        text: action.payload,
      };
    }
    case 'PROCESSING': {
      return {
        ...state,
        processing: action.payload,
      };
    }
    case 'SET_ROOT_WORD': {
      return {
        ...state,
        rootWord: action.payload,
      };
    }
    case 'SET_ROOT_WORD_INDICES': {
      return {
        ...state,
        rootWordStart: action.payload.start,
        rootWordEnd: action.payload.end,
      };
    }
    case 'SET_COMPARISON_PX': {
      return {
        ...state,
        comparisonPxScores: action.payload,
      };
    }
    case 'SET_SCORE_AND_PERCENTILE': {
      return {
        ...state,
        cosineSimScore: action.payload.cosineSimScore,
        cosineSimPercentile: action.payload.cosineSimPercentile,
        distanceScore: action.payload.distanceScore,
        distancePercentile: action.payload.distancePercentile,
      };
    }
    case 'SET_AVERAGE_PX': {
      return {
        ...state,
        averagePxScores: action.payload,
      };
    }
    case 'SET_PX': {
      return {
        ...state,
        pxScores: action.payload,
      };
    }
    case 'COMPUTE_PX': {
      return {
        ...state,
        computePxScores: action.payload,
      };
    }
    case 'ADD_WORD': {
      const { word, vector, freq } = action.payload;
      const nextWordsVector = {
        ...state.wordsVector,
        [word]: vector,
      };
      const nextWordsFreq = {
        ...state.wordsFreq,
        [word]: freq,
      };
      return {
        ...state,
        wordsVector: nextWordsVector,
        wordsFreq: nextWordsFreq,
      };
    }
    case 'REMOVE_WORD': {
      const nextWordsVector = {
        ...state.wordsVector,
      };
      const nextWordsFreq = {
        ...state.wordsFreq,
      };
      if (nextWordsFreq[action.payload.word] - action.payload.freq === 0) {
        delete nextWordsVector[action.payload.word];
        delete nextWordsFreq[action.payload.word];
      } else {
        nextWordsFreq[action.payload.word] -= action.payload.freq;
      }
      return {
        ...state,
        wordsVector: nextWordsVector,
        wordsFreq: nextWordsFreq,
      };
    }
    case 'SET_TRAIT_COEFFS': {
      const { traitCodes, traitCoeffs, traitIntercepts } = action.payload;
      return {
        ...state,
        traitCodes,
        traitCoeffs,
        traitIntercepts,
      };
    }
    case 'SET_TRAITS': {
      return {
        ...state,
        traits: action.payload,
      };
    }
    case 'SET_TEXT': {
      return {
        ...state,
        text: action.payload,
      };
    }
    case 'APPEND_WORD': {
      return {
        ...state,
        text: `${state.text} ${action.payload}`,
      };
    }
    case 'ADD_TO_CACHE': {
      return {
        ...state,
        wordVectorCache: {
          ...state.wordVectorCache,
          [action.payload.word]: action.payload.vector,
        },
      };
    }
    case 'SET_CENTROID': {
      return {
        ...state,
        centroidVector: action.payload.centroidVector,
        totalWords: action.payload.totalWords,
      };
    }
    case 'READY': {
      return {
        ...state,
        ready: true,
      };
    }
    default:
      return state;
  }
};

const refs = [];
function WordFormatter({ word, start, end }) {
  const ref = useRef(null);

  useEffect(() => {
    if (ref && ref.current) {
      refs.push(ref);
    }
  }, [ref]);

  return (
    <Div tag="span" style={{ whiteSpace: 'nowrap' }} ref={ref} data-start={start} data-end={end}>
      {word}
    </Div>
  );
}

WordFormatter.propTypes = {
  word: PropTypes.string.isRequired,
};

const htmlFormatter = (leftOffset, topOffset, text) => {
  const elements = [];
  const br = <span> </span>;
  let word = '';
  let startIndex = 0;
  let endIndex = 0;
  for (let i = 0; i < text.length; i += 1) {
    if (text[i] === ' ') {
      if (word) {
        endIndex = i;
        elements.push(<WordFormatter word={word} start={startIndex} end={endIndex} />);
      }
      elements.push(br);
      word = '';
      startIndex = i + 1;
    } else if (text[i] === '\n') {
      if (word) {
        endIndex = i;
        elements.push(<WordFormatter word={word} start={startIndex} end={endIndex} />);
      }
      elements.push(<br />);
      word = '';
      startIndex = i + 1;
    } else {
      word += text[i];
    }
  }
  if (word) {
    endIndex = text.length;
    elements.push(<WordFormatter word={word} start={startIndex} end={endIndex} />);
  }
  return elements;
};

/**
 *
 * Diagnostic Methiods
 *
 */

const getWordTraitProbabilities = async (word, traitCodes, traits, traitCoeffs, traitIntercepts, pxModel) => {
  const resp = await getWordVector(word, false, WVM_ID[pxModel]);
  if (resp.vector.length === NUM_COEFFICIENTS[pxModel]) {
    const px = await getTraitProbabilitiesFromWordVec(resp.vector, traitCodes, traits, traitCoeffs, traitIntercepts);
    logTraitProbabilities(`PX of ${word} is`, traitCodes, px);
    return px;
  }
  return [];
};

const getWordPx = async (word, traitCodes, traits, traitCoeffs, traitIntercepts, pxModel) => {
  const resp = await getWordVectors(word, WVM_ID[pxModel]);
  if (resp[0].vector.length === NUM_COEFFICIENTS[pxModel]) {
    const px = await getPxFromWordVec(resp[0].vector, traitCodes, traits, traitCoeffs, traitIntercepts);
    logPxScores(`PX of ${word} is`, traitCodes, px);
    return px;
  }
  return [];
};

const getSentencePx = async (sentence, traitCodes, traits, traitCoeffs, traitIntercepts, pxModel, text) => {
  const words = sentence
    .toLowerCase()
    .replace(/[^\w\s]/gi, ' ')
    .trim()
    .split(/\W+/);
  const wordsFreq = {};
  const wordsVector = {};
  for (let i = 0; i < words.length; i += 1) {
    wordsFreq[words[i]] = (wordsFreq[words[i]] || 0) + 1;
    if (!wordsVector[words[i]]) {
      // eslint-disable-next-line no-await-in-loop
      const resp = await getWordVector(words[i], false, WVM_ID[pxModel]);
      wordsVector[words[i]] = resp.vector;
    }
  }
  const { totalWords, centroidVector } = await getCentroidVector(text, wordsVector, wordsFreq, pxModel);
  const px = await getPxFromWordVec(centroidVector, traitCodes, traits, traitCoeffs, traitIntercepts);
  logPxScores(`PX of ${sentence} is`, traitCodes, px);
  return px;
};

const PxTextEditor = forwardRef(
  (
    { comparisonPxScores, onPxScoresChange, onSimilarityChange, onModelChange, onError, enableDiagnostics, ...rest },
    pxRef
  ) => {
    const initialState = {
      past: [],
      future: [],
      historicalText: '',
      text: '',
      wordsVector: {},
      wordsFreq: {},
      traitCoeffs: {},
      traitCodes: [],
      traitIntercepts: {},
      pxScores: [],
      traits: {},
      computePxScores: false,
      processing: false,
      averagePxScores: [],
      comparisonPxScores: [],
      isRelatedPopupOpen: false,
      relatedPopupCoords: { top: 0, left: 0 },
      relatedPopupIndex: 0,
      rootWord: '',
      rootWordStart: 0,
      rootWordEnd: 0,
      hasError: false,
      error: null,
      wordVectorCache: {},
      centroidVector: [],
      totalWords: 0,
      cosineSimScore: null,
      cosineSimPercentile: null,
      distanceScore: null,
      distancePercentile: null,
      ready: false,
      popupModel: MASKED_MODEL,
      pxModel: DEFAULT_MODEL,
    };
    const [state, dispatch] = useReducer(reducer, initialState);
    const textAreaRef = useRef(null);
    const [, forceRender] = useState(false);
    const distanceDist = getWordDistancesFromMap(WVM_ID[state.pxModel], 3);

    const keydownHandler = (e) => {
      if (e.keyCode === 90 && (e.ctrlKey || e.metaKey) && e.shiftKey) {
        console.log('REDO');
        dispatch({ type: 'REDO' });
        dispatch({ type: 'COMPUTE_PX', payload: true });
      } else if (e.keyCode === 90 && (e.ctrlKey || e.metaKey)) {
        console.log('UNDO');
        dispatch({ type: 'UNDO' });
        dispatch({ type: 'COMPUTE_PX', payload: true });
      }
    };

    useImperativeHandle(pxRef, () => ({
      addWord: (word) => {
        console.log('Need to add word', word);
        const nextText = `${state.text} ${word}`;
        dispatch({ type: 'SET_TEXT', payload: nextText });
        onTextChange(state, dispatch, nextText);
      },
    }));

    useEffect(() => {
      if (state.traitCodes.length > 0 && comparisonPxScores.length > 0) {
        const clonedScores = comparisonPxScores.filter(({ trait: { subType } }) => subType !== 'factor').slice();
        clonedScores.sort(
          ({ trait: traitA }, { trait: traitB }) =>
            state.traitCodes.indexOf(traitA.code) - state.traitCodes.indexOf(traitB.code)
        );
        // console.log('Received a new target PX');
        dispatch({ type: 'SET_COMPARISON_PX', payload: clonedScores });
      }
      if (comparisonPxScores.length === 0) {
        // console.log('Unsetting target PX');
        dispatch({ type: 'SET_COMPARISON_PX', payload: [] });
      }
    }, [comparisonPxScores, state.traitCodes, dispatch]);

    useEffect(() => {
      if (state.hasError) {
        onError('Something is not right. Please try again later', state.error);
        dispatch({ type: 'SET_ERROR', payload: false });
      }
      // NOTE: needs to run once on load
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.hasError]);

    useEffect(() => {
      if (state.pxScores.length > 0) {
        // logPxScores('Copy Px', state.traitCodes, state.pxScores);
        // logPxScores('Dataset Px', state.traitCodes, state.comparisonPxScores);
        const { cosineSimScore, cosineSimPercentile } = computeCosineSimilarityPercentile(
          state,
          state.pxScores,
          state.comparisonPxScores
        );
        const { distanceScore, distancePercentile } = computeDistancePercentile(
          state,
          state.pxScores,
          state.comparisonPxScores
        );
        dispatch({
          type: 'SET_SCORE_AND_PERCENTILE',
          payload: {
            cosineSimScore,
            cosineSimPercentile,
            distanceScore,
            distancePercentile,
          },
        });
        onSimilarityChange(0, distancePercentile, 100);
        /*
      const cosineSimScore = cosineSimilarity(
        state.traitCodes,
        state.pxScores,
        clonedScores,
      );
      const cosineSimLow = cosineSimilarity(
        state.traitCodes,
        state.averagePxScores,
        clonedScores,
      );
      const cosineSimHigh = 1;
      console.log('Average Px');
      console.log(state.averagePxScores);
      console.log('Text Px');
      console.log(state.pxScores);
      console.log('Dataset Px');
      console.log(clonedScores);
      console.log('Scores are');
      console.log(cosineSimLow, cosineSimScore, cosineSimHigh);
      onSimilarityChange(cosineSimLow, cosineSimScore, cosineSimHigh);
      */
      }
      // NOTE: needs to run once on load
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.comparisonPxScores]);

    useEffect(() => {
      const f = async () => {
        try {
          onModelChange(DEFAULT_MODEL);
          const traitCoeffs = {};
          const traitIntercepts = {};
          const models = await getWordModel(DEFAULT_REFRESH[state.pxModel], SET_ID[state.pxModel]);
          const traitsList = await getTraits();
          const traitCodes = [];
          const traits = {};
          traitsList.forEach((trait) => {
            traits[trait.code] = trait;
          });
          models
            .filter(({ trait: { code } }) => code)
            .forEach(({ trait: { code }, coefficients, intercept }) => {
              traitCodes.push(code);
              traitCoeffs[code] = coefficients;
              traitIntercepts[code] = intercept;
            });
          dispatch({
            type: 'SET_TRAIT_COEFFS',
            payload: {
              traitCodes,
              traitCoeffs,
              traitIntercepts,
            },
          });
          dispatch({ type: 'SET_TRAITS', payload: traits });
          const averagePxScores = await getAveragePxScores(traitCodes, traits);
          dispatch({ type: 'SET_AVERAGE_PX', payload: averagePxScores });
          dispatch({ type: 'READY' });
        } catch (error) {
          dispatch({ type: 'SET_ERROR', payload: { hasError: true, error } });
        }
      };
      f();
      // NOTE: needs to run once on load
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    useEffect(() => {
      if (localStorage.getItem('COPYPX_DIAGNOSTIC') && enableDiagnostics && state.ready) {
        console.log('Enabling diagnostic methods');
        console.log('`await getWordTraitProbabilities(word)` return raw px probabilities of the word');
        window.getWordTraitProbabilities = (word) =>
          getWordTraitProbabilities(
            word,
            state.traitCodes,
            state.traits,
            state.traitCoeffs,
            state.traitIntercepts,
            state.pxModel
          );
        console.log('`await getWordPx(word)` return px of the word');
        window.getWordPx = (word) =>
          getWordPx(word, state.traitCodes, state.traits, state.traitCoeffs, state.traitIntercepts, state.pxModel);
        console.log('`await getSentencePx(sentence)` return px of the sentence');
        window.getSentencePx = (sentence) =>
          getSentencePx(
            sentence,
            state.traitCodes,
            state.traits,
            state.traitCoeffs,
            state.traitIntercepts,
            state.pxModel,
            state.text
          );
        console.log('`getCosineSimilarity(px1, px2)` returns cosine similarity and percentile of two pxs');
        window.getCosineSimilarity = (px1, px2) =>
          computeCosineSimilarityPercentile({ pxModel: state.pxModel, traitCodes: state.traitCodes }, px1, px2);
        console.log('`getRMSE(px1, px2)` returns RMSE and RMSE percentile of two pxs');
        window.getRMSE = (px1, px2) =>
          computeDistancePercentile({ pxModel: state.pxModel, traitCodes: state.traitCodes }, px1, px2);
        console.log('`getTargetMetrics(px) returns the similarity measures of the px with selected dataset`');
        window.getTargetMetrics = (px) => {
          if (state.comparisonPxScores.length > 0) {
            const scores1 = computeCosineSimilarityPercentile(
              { traitCodes: state.traitCodes },
              px,
              state.comparisonPxScores
            );
            const scores2 = computeDistancePercentile({ traitCodes: state.traitCodes }, px, state.comparisonPxScores);
            return {
              ...scores1,
              ...scores2,
            };
          }
          return {};
        };
      }
      // NOTE: needs to run once on load
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [enableDiagnostics, state.ready, state.comparisonPxScores]);

    useEffect(() => {
      const f = async () => {
        try {
          const { totalWords, centroidVector } = await getCentroidVector(
            state.text,
            state.wordsVector,
            state.wordsFreq,
            state.pxModel
          );
          if (totalWords < WORDS_MINIMUM_BOUND[state.pxModel]) {
            // There are no words with a vector
            // so set PX to be empty
            console.log('No words with valid PX found. Unsetting PX');
            dispatch({ type: 'SET_PX', payload: [] });
          } else {
            dispatch({ type: 'SET_CENTROID', payload: { centroidVector, totalWords } });
            const traitProbabilities = await getTraitProbabilities(
              centroidVector,
              state.traitCodes,
              state.traits,
              state.traitCoeffs,
              state.traitIntercepts
            );
            // logPxScores('Copy PX Probabilities', state.traitCodes, traitProbabilities);
            const pxScores = await getPxFromWordVec(
              centroidVector,
              state.traitCodes,
              state.traits,
              state.traitCoeffs,
              state.traitIntercepts
            );
            // logPxScores('Copy Px', state.traitCodes, pxScores);
            if (state.comparisonPxScores.length > 0) {
              // logPxScores('Dataset Px', state.traitCodes, state.comparisonPxScores);
              const { cosineSimScore, cosineSimPercentile } = computeCosineSimilarityPercentile(
                state,
                pxScores,
                state.comparisonPxScores
              );
              const { distanceScore, distancePercentile } = computeDistancePercentile(
                state,
                pxScores,
                state.comparisonPxScores
              );
              dispatch({
                type: 'SET_SCORE_AND_PERCENTILE',
                payload: {
                  cosineSimScore,
                  cosineSimPercentile,
                  distanceScore,
                  distancePercentile,
                },
              });
              onSimilarityChange(0, distancePercentile, 100);
              /*
              const cosineSimScore = cosineSimilarity(
                state.traitCodes,
                pxScores,
                state.comparisonPxScores,
              );
              const cosineSimLow = cosineSimilarity(
                state.traitCodes,
                state.averagePxScores,
                state.comparisonPxScores,
              );
              const cosineSimHigh = 1;
              console.log('Average Px');
              console.log(state.averagePxScores);
              console.log('Text Px');
              console.log(pxScores);
              console.log('Dataset Px');
              console.log(state.comparisonPxScores);
              console.log('Scores are');
              console.log(cosineSimLow, cosineSimScore, cosineSimHigh);
              onSimilarityChange(cosineSimLow, cosineSimScore, cosineSimHigh);
              */
            }
            dispatch({ type: 'SET_PX', payload: pxScores });
          }
        } catch (error) {
          console.log(error);
          dispatch({ type: 'SET_ERROR', payload: { hasError: true, error } });
          dispatch({ type: 'SET_PX', payload: [] });
        } finally {
          dispatch({ type: 'COMPUTE_PX', payload: false });
          dispatch({ type: 'PROCESSING', payload: false });
        }
      };
      if (state.computePxScores) {
        f();
      }
      // NOTE: needs to run once on load
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.computePxScores]);

    useEffect(() => {
      console.log('Copy PX has changed');
      onPxScoresChange(state.pxScores);
      // NOTE: needs to run once on load
      // eslint-disable-next-line react-hooks/exhaustive-deps
    }, [state.pxScores]);

    useEffect(() => {
      if (textAreaRef.current) {
        const onScroll = (e) => {
          forceRender((x) => !x);
        };
        textAreaRef.current.onscroll = onScroll;
      }
    }, [textAreaRef]);

    useEffect(() => {
      document.addEventListener('keydown', keydownHandler);
      return () => {
        document.removeEventListener('keydown', keydownHandler);
      };
    }, []);

    return (
      <Div pos="relative" {...rest}>
        <Div
          pos="absolute"
          top="0px"
          left="0px"
          p={{ l: '0.75rem', r: '2rem', y: '0.5rem' }}
          textSize="title"
          border="1px transparent"
          textWeight="500"
          w="100%"
          h="calc(100% - 2rem)"
          overflow="scroll"
          style={{
            fontFamily: 'Roboto',
            wordBreak: 'normal',
            overflowWrap: 'break-word',
            writingMode: 'horizontal-tb',
            whiteSpace: 'pre-wrap',
            opacity: '0',
          }}
        >
          {htmlFormatter(0, 0, state.text)}
        </Div>
        <Textarea
          className="data-hj-allow"
          ref={textAreaRef}
          p={{ l: '0.75rem', r: '2rem', y: '0.5rem' }}
          textSize="title"
          border="1px solid"
          textWeight="400"
          style={{
            resize: 'none',
            fontFamily: 'Roboto',
            background: 'transparent',
            opacity: '1',
          }}
          value={state.text}
          isLoading={Object.keys(state.traits).length === 0 || state.traitCodes.length === 0 || state.processing}
          loadingIcon={<Icon name="Loading" pos="absolute" top=".8rem" right="0.8rem" size="24px" color="brand500" />}
          onChange={(e) => {
            while (timeoutsQueue.length) {
              const x = timeoutsQueue.pop();
              clearInterval(x);
            }
            const { value } = e.target;
            const fmtValue = value.replace('-', '‑');
            dispatch({ type: 'SET_TEXT', payload: fmtValue });
            const interval = setTimeout(async () => {
              timeoutsQueue.pop();
              onTextChange(state, dispatch, value);
            }, 600);
            timeoutsQueue.push(interval);
          }}
          h="calc(100% - 2rem)"
        />
        <Text textSize="body" p={{ y: '0.5rem' }}>
          <Text tag="span" textSize="body" textWeight="bold">
            Tip
          </Text>
          : Use{' '}
          <Text tag="span" textSize="body" textWeight="bold">
            {navigator.platform.indexOf('Mac') > -1 ? 'Cmd' : 'Ctrl'} + Z
          </Text>{' '}
          and{' '}
          <Text tag="span" textSize="body" textWeight="bold">
            Shift + {navigator.platform.indexOf('Mac') > -1 ? 'Cmd' : 'Ctrl'} + Z
          </Text>{' '}
          to undo and redo your changes
        </Text>
        {localStorage.getItem('COPYPX_DIAGNOSTIC') && enableDiagnostics ? (
          <Div p="0.5rem" pos="absolute" bottom="1rem" right="1rem" bg="black50" textColor="white">
            <Div p={{ y: '0.2rem' }}>
              Cosine Similarity: &nbsp;
              {state.cosineSimScore ? state.cosineSimScore.toFixed(3) : '-'}
            </Div>
            <Div p={{ y: '0.2rem' }}>
              Cosine Percentile (Progress Bar): &nbsp;
              {state.cosineSimPercentile ? state.cosineSimPercentile.toFixed(3) : '-'}
            </Div>
            <Div p={{ y: '0.2rem' }}>
              RMSE: &nbsp;
              {state.distanceScore ? state.distanceScore.toFixed(3) : '-'}
            </Div>
            <Div p={{ y: '0.2rem' }}>
              RMSE Percentile (Progress Bar): &nbsp;
              {state.distancePercentile ? state.distancePercentile.toFixed(3) : '-'}
            </Div>
          </Div>
        ) : null}
        <>
          {refs
            .filter((ref) => ref.current)
            .map((ref, index) => {
              let start = 0;
              let end = 0;
              if (ref.current.attributes['data-start']) {
                start = Number.parseInt(ref.current.attributes['data-start'].value, 10);
              }
              if (ref.current.attributes['data-end']) {
                end = Number.parseInt(ref.current.attributes['data-end'].value, 10);
              }
              const rect = ref.current.getBoundingClientRect();
              const textAreaRect = textAreaRef.current.getBoundingClientRect();
              const {
                current: { scrollTop },
              } = textAreaRef;
              return (
                <Div
                  pos="absolute"
                  w={`${rect.width}px`}
                  h={`${rect.height}px`}
                  top={`${rect.top - textAreaRect.top - scrollTop}px`}
                  left={`${rect.left - textAreaRect.left}px`}
                  cursor="pointer"
                  bg="transparent"
                  hoverBg="warning500"
                  opacity="0.3"
                  onClick={async () => {
                    const word = ref.current.innerText;
                    dispatch({
                      type: 'OPEN_RELATED_POPUP',
                      payload: {
                        top: rect.top - textAreaRect.top + rect.height - scrollTop,
                        left: rect.left - textAreaRect.left,
                        relatedPopupIndex: index,
                      },
                    });
                    try {
                      dispatch({ type: 'PROCESSING', payload: true });
                      dispatch({ type: 'SET_ROOT_WORD', payload: word });
                      dispatch({
                        type: 'SET_ROOT_WORD_INDICES',
                        payload: { start, end },
                      });
                    } catch (error) {
                      console.log(error);
                      // Ignore the error
                      dispatch({ type: 'CLOSE_RELATED_POPUP' });
                      dispatch({ type: 'SET_ERROR', payload: { hasError: true, error } });
                    } finally {
                      dispatch({ type: 'PROCESSING', payload: false });
                    }
                  }}
                />
              );
            })}
          {state.isRelatedPopupOpen && state.rootWord ? (
            <Popup
              top={`${state.relatedPopupCoords.top}px`}
              left={`${state.relatedPopupCoords.left}px`}
              rootWord={state.rootWord}
              enableDiagnostics={enableDiagnostics}
              // distance/similarity between copy and dataset
              copyTargetDistance={state.distanceScore}
              // distancee/similarity distribution
              distanceDist={distanceDist}
              onClose={() => {
                dispatch({ type: 'CLOSE_RELATED_POPUP' });
              }}
              onClick={async (replacementWord, vector) => {
                console.log('Replacing word at', state.relatedPopupIndex, 'with', replacementWord);
                dispatch({ type: 'ADD_TO_CACHE', payload: { word: replacementWord, vector } });
                let counter = 0;
                let lastCharIsWhitespace = false;
                let skipText = counter === state.relatedPopupIndex;
                let newText = skipText ? replacementWord : '';
                for (let i = 0; i < state.text.length; i += 1) {
                  if (state.text[i] === ' ' || state.text[i] === '\n') {
                    if (!lastCharIsWhitespace) {
                      counter += 1;
                    }
                    skipText = false;
                    lastCharIsWhitespace = true;
                  } else {
                    lastCharIsWhitespace = false;
                  }
                  if (!skipText) {
                    newText += state.text[i];
                  }
                  if (counter === state.relatedPopupIndex && !skipText) {
                    skipText = true;
                    newText += replacementWord;
                  }
                }
                dispatch({ type: 'SET_TEXT', payload: newText });
                onTextChange(state, dispatch, newText);
                dispatch({ type: 'CLOSE_RELATED_POPUP' });
              }}
              popupModel={state.popupModel}
              state={state}
              dispatch={dispatch}
            />
          ) : null}
        </>
      </Div>
    );
  }
);

PxTextEditor.propTypes = {
  comparisonPxScores: PropTypes.arrayOf(PropTypes.shape({})),
  onSimilarityChange: PropTypes.func,
  onPxScoresChange: PropTypes.func,
  onError: PropTypes.func,
  enableDiagnostics: PropTypes.bool,
};

PxTextEditor.defaultProps = {
  comparisonPxScores: [],
  onSimilarityChange: () => null,
  onPxScoresChange: () => null,
  onError: () => null,
  enableDiagnostics: false,
};

export default PxTextEditor;
